import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Alert from "../../components/common/Alert";
import Badge from "../../components/common/Badge";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Select from "../../components/common/Select";
import Table from "../../components/common/Table";
import {
  useGetDeviceHealthQuery,
  useGetLocalDevicesQuery,
  usePostDeviceHeartbeatMutation,
} from "../../store/api";
import { formatApiDateTime } from "../../utils/dateTime";

const DEVICE_TYPES = ["POS", "KIOSK", "KDS", "DISPLAY"];
const HEALTH_STATUSES = ["ONLINE", "WARNING", "OFFLINE"];
const POLLING_INTERVAL_MS = 45000;

const STATUS_BADGE_TONE = {
  ONLINE: "success",
  WARNING: "warning",
  OFFLINE: "danger",
  DEGRADED: "warning",
  UNREACHABLE: "danger",
  UNKNOWN: "neutral",
};

const getStatusTone = (status) => STATUS_BADGE_TONE[status] || "neutral";

const getSystemState = (queryState, lastLoadedSnapshot) => {
  const { data, error, isError } = queryState;

  if (data) {
    return {
      mode: data.overallStatus === "DEGRADED" ? "degraded" : "healthy",
      payload: data,
      staleSnapshot: lastLoadedSnapshot,
    };
  }

  if (isError && error?.status === 503 && error?.data?.databaseStatus === "OFFLINE") {
    return {
      mode: "degraded",
      payload: error.data,
      staleSnapshot: lastLoadedSnapshot,
    };
  }

  if (isError && error?.status === 404) {
    return {
      mode: "unsupported",
      payload: null,
      staleSnapshot: lastLoadedSnapshot,
    };
  }

  if (isError) {
    return {
      mode: "unreachable",
      payload: null,
      staleSnapshot: lastLoadedSnapshot,
    };
  }

  return {
    mode: "loading",
    payload: null,
    staleSnapshot: lastLoadedSnapshot,
  };
};

const formatMinutesSinceSeen = (value) => {
  if (value === null || value === undefined) return "Never";
  if (value <= 0) return "Just now";
  if (value === 1) return "1 minute";
  return `${value} minutes`;
};

const formatErrorMessage = (error, fallback) =>
  error?.data?.message ||
  error?.data?.detail ||
  error?.error ||
  fallback;

const MetricCard = ({ label, value, tone, helper }) => (
  <Card className="relative overflow-hidden p-5">
    <div
      className={[
        "absolute inset-x-0 top-0 h-1",
        tone === "success"
          ? "bg-emerald-500"
          : tone === "warning"
            ? "bg-amber-500"
            : tone === "danger"
              ? "bg-rose-500"
              : "bg-slate-300",
      ].join(" ")}
    />
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
        {value}
      </div>
      {helper && (
        <div className="text-sm text-slate-500 dark:text-slate-400">{helper}</div>
      )}
    </div>
  </Card>
);

const DeviceHealth = () => {
  const user = useSelector((state) => state.auth.user);
  const [filters, setFilters] = useState({ type: "", status: "" });
  const [lastLoadedSnapshot, setLastLoadedSnapshot] = useState(null);
  const [heartbeatModal, setHeartbeatModal] = useState({
    isOpen: false,
    deviceId: null,
    deviceName: "",
  });
  const [heartbeatFeedback, setHeartbeatFeedback] = useState(null);

  const canManuallyHeartbeat = String(user?.accessLevel || "")
    .toUpperCase()
    .includes("ADMIN");

  const queryArgs = useMemo(
    () => ({
      ...(filters.type ? { type: filters.type } : {}),
      ...(filters.status ? { status: filters.status } : {}),
    }),
    [filters],
  );

  const healthQuery = useGetDeviceHealthQuery(queryArgs, {
    pollingInterval: POLLING_INTERVAL_MS,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const shouldUseLocalDevicesFallback =
    healthQuery.isError && healthQuery.error?.status === 404;
  const localDevicesQuery = useGetLocalDevicesQuery(filters.type || undefined, {
    skip: !shouldUseLocalDevicesFallback,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [postHeartbeat, heartbeatState] = usePostDeviceHeartbeatMutation();

  useEffect(() => {
    if (healthQuery.data) {
      setLastLoadedSnapshot(healthQuery.data);
    }
  }, [healthQuery.data]);

  const systemState = getSystemState(healthQuery, lastLoadedSnapshot);
  const healthPayload = systemState.payload;
  const fallbackDevices = useMemo(() => {
    const localDevices = Array.isArray(localDevicesQuery.data)
      ? localDevicesQuery.data
      : [];

    return localDevices
      .filter((device) => {
        if (filters.status) {
          return false;
        }
        return device?.isActive !== false;
      })
      .map((device) => ({
        ...device,
        healthStatus: "UNAVAILABLE",
        lastSeenAt: null,
        minutesSinceLastSeen: null,
      }));
  }, [localDevicesQuery.data, filters.status]);

  const summary =
    systemState.mode === "healthy"
      ? healthPayload?.summary
      : systemState.mode === "unsupported"
        ? {
            totalDevices: fallbackDevices.length,
            onlineDevices: 0,
            warningDevices: 0,
            offlineDevices: 0,
          }
        : null;
  const devices =
    systemState.mode === "healthy"
      ? healthPayload?.devices || []
      : systemState.mode === "unsupported"
        ? fallbackDevices
        : [];
  const lastKnownCheckedAt =
    healthPayload?.checkedAt || lastLoadedSnapshot?.checkedAt || null;

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenHeartbeat = (device) => {
    setHeartbeatFeedback(null);
    setHeartbeatModal({
      isOpen: true,
      deviceId: device.deviceId,
      deviceName: device.deviceName || "device",
    });
  };

  const handleConfirmHeartbeat = async () => {
    try {
      const response = await postHeartbeat(heartbeatModal.deviceId).unwrap();
      setHeartbeatFeedback({
        tone: "success",
        message: `${heartbeatModal.deviceName} checked in successfully at ${formatApiDateTime(
          response?.lastSeenAt,
          "the latest timestamp",
        )}.`,
      });
      setHeartbeatModal({ isOpen: false, deviceId: null, deviceName: "" });
      healthQuery.refetch();
    } catch (error) {
      setHeartbeatFeedback({
        tone: "error",
        message: formatErrorMessage(
          error,
          "Heartbeat failed. Refresh the dashboard and verify the device is active and in the current store.",
        ),
      });
    }
  };

  const columns = [
    { key: "deviceName", label: "Device Name" },
    { key: "deviceType", label: "Type" },
    { key: "healthStatus", label: "Health" },
    { key: "lastSeenAt", label: "Last Seen" },
    { key: "minutesSinceLastSeen", label: "Minutes Since Seen" },
    { key: "ipAddress", label: "IP Address" },
  ];

  const rows = devices.map((device) => ({
    id: device.deviceId,
    deviceName: (
      <span className="font-semibold text-slate-900 dark:text-slate-100">
        {device.deviceName || "Unnamed Device"}
      </span>
    ),
    deviceType: device.deviceType || "-",
    healthStatus: (
      <Badge tone={getStatusTone(device.healthStatus)}>
        {device.healthStatus || "UNKNOWN"}
      </Badge>
    ),
    lastSeenAt: formatApiDateTime(device.lastSeenAt, "Never"),
    minutesSinceLastSeen: formatMinutesSinceSeen(device.minutesSinceLastSeen),
    ipAddress: device.ipAddress || "-",
  }));

  const banner = (() => {
    if (systemState.mode === "degraded") {
      return (
        <Alert tone="warning">
          <div className="space-y-2">
            <div className="font-semibold">System is degraded.</div>
            <div>
              {healthPayload?.message ||
                "Database connectivity is unavailable, so device health cannot be calculated reliably."}
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge tone={getStatusTone(healthPayload?.apiStatus)}>
                API: {healthPayload?.apiStatus || "UNKNOWN"}
              </Badge>
              <Badge tone={getStatusTone(healthPayload?.databaseStatus)}>
                Database: {healthPayload?.databaseStatus || "UNKNOWN"}
              </Badge>
              <Badge tone={getStatusTone(healthPayload?.overallStatus)}>
                Overall: {healthPayload?.overallStatus || "UNKNOWN"}
              </Badge>
            </div>
          </div>
        </Alert>
      );
    }

    if (systemState.mode === "unreachable") {
      return (
        <Alert tone="error">
          <div className="space-y-2">
            <div className="font-semibold">
              Edge API is unreachable. Device health cannot be refreshed.
            </div>
            <div>
              {formatErrorMessage(
                healthQuery.error,
                "The API did not return a usable health payload.",
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge tone="danger">API: UNREACHABLE</Badge>
              <Badge tone="neutral">Database: UNKNOWN</Badge>
              <Badge tone="danger">Overall: UNREACHABLE</Badge>
            </div>
          </div>
        </Alert>
      );
    }

    if (systemState.mode === "unsupported") {
      return (
        <Alert tone="warning">
          <div className="space-y-2">
            <div className="font-semibold">
              This Edge environment does not expose the device health API yet.
            </div>
            <div>
              The deployed swagger does not publish <code>/api/edge/devices/health</code>, so the
              dashboard is showing registered active devices from <code>local-devices</code> instead
              of live heartbeat status.
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge tone="neutral">API Feature: Not Available</Badge>
              <Badge tone="neutral">Live Status: Unsupported</Badge>
            </div>
          </div>
        </Alert>
      );
    }

    if (systemState.mode === "healthy" && healthPayload?.message) {
      return <Alert tone="info">{healthPayload.message}</Alert>;
    }

    return null;
  })();

  const summaryCards = [
    {
      label: "Total Devices",
      value: summary?.totalDevices ?? "-",
      tone: "neutral",
      helper: "Active devices in the current store.",
    },
    {
      label: "Online",
      value: summary?.onlineDevices ?? "-",
      tone: "success",
      helper: "Heartbeat seen within the last 2 minutes.",
    },
    {
      label: "Warning",
      value: summary?.warningDevices ?? "-",
      tone: "warning",
      helper: "Last heartbeat is older than 2 minutes.",
    },
    {
      label: "Offline",
      value: summary?.offlineDevices ?? "-",
      tone: "danger",
      helper: "No recent heartbeat or the device never checked in.",
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Device Health"
        subtitle="Monitor Edge device check-ins, summary counts, and system availability."
        actions={
          <div className="flex flex-wrap gap-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => healthQuery.refetch()}
              disabled={healthQuery.isFetching}
            >
              {healthQuery.isFetching ? "Refreshing..." : "Refresh Now"}
            </Button>
          </div>
        }
      />

      {heartbeatFeedback && (
        <Alert
          tone={heartbeatFeedback.tone}
          onClose={() => setHeartbeatFeedback(null)}
        >
          {heartbeatFeedback.message}
        </Alert>
      )}

      {banner}

      {lastKnownCheckedAt && (
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Last refreshed: {formatApiDateTime(lastKnownCheckedAt)}
          {systemState.mode !== "healthy" ? " (latest successful or degraded check)" : ""}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => (
          <MetricCard
            key={card.label}
            label={card.label}
            value={card.value}
            tone={card.tone}
            helper={card.helper}
          />
        ))}
      </div>

      <Card className="p-0">
        <div className="border-b border-slate-100 px-6 py-4 dark:border-slate-700">
          <div className="grid gap-4 md:grid-cols-[minmax(0,220px)_minmax(0,220px)_1fr]">
            <Select
              label="Filter by type"
              value={filters.type}
              onChange={(event) => handleFilterChange("type", event.target.value)}
            >
              <option value="">All types</option>
              {DEVICE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
            <Select
              label="Filter by status"
              value={filters.status}
              onChange={(event) => handleFilterChange("status", event.target.value)}
            >
              <option value="">All statuses</option>
              {HEALTH_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>
            {/* <div className="flex items-end justify-start md:justify-end">
              <div className="rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {systemState.mode === "unsupported"
                  ? "Live health endpoint not available"
                  : `Polling every ${Math.round(POLLING_INTERVAL_MS / 1000)} seconds`}
              </div>
            </div> */}
          </div>
        </div>

        <div className="px-6 py-4">
          {healthQuery.isLoading && !lastLoadedSnapshot ? (
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Loading device health...
            </div>
          ) : systemState.mode === "degraded" ? (
            <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 px-4 py-6 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              Device health cannot be calculated because the database is unavailable.
            </div>
          ) : systemState.mode === "unreachable" ? (
            <div className="rounded-lg border border-dashed border-rose-300 bg-rose-50 px-4 py-6 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
              The Edge API did not return current device data. Refresh once connectivity is restored.
            </div>
          ) : systemState.mode === "unsupported" && localDevicesQuery.isLoading ? (
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Loading registered devices...
            </div>
          ) : systemState.mode === "unsupported" && localDevicesQuery.isError ? (
            <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 px-4 py-6 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              {formatErrorMessage(
                localDevicesQuery.error,
                "The device registry is available in this build, but it could not be loaded right now.",
              )}
            </div>
          ) : (
            <Table
              columns={columns}
              rows={rows}
              emptyText={
                systemState.mode === "unsupported"
                  ? filters.status
                    ? "Status filtering needs the dedicated health API, which is not available on this environment."
                    : "No active registered devices matched the selected type."
                  : "No active devices matched the selected filters."
              }
            />
          )}
        </div>
      </Card>

      <Modal
        isOpen={heartbeatModal.isOpen}
        onClose={() =>
          setHeartbeatModal({ isOpen: false, deviceId: null, deviceName: "" })
        }
        title="Send Manual Heartbeat"
        confirmText={heartbeatState.isLoading ? "Sending..." : "Send Heartbeat"}
        onConfirm={handleConfirmHeartbeat}
      >
        <div className="space-y-3">
          <p>
            Send a manual heartbeat for <strong>{heartbeatModal.deviceName}</strong>?
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            This QA action updates the device&apos;s latest check-in timestamp and may temporarily make it
            appear online.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default DeviceHealth;
